import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buyersApi, listingsApi, type ListingSummaryDto } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, Input, Skeleton, useToast } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import {
  listingShortName,
  resolvedShortListingName,
  shortenListingName,
} from "@/lib/listingDisplay";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { buyerKeys, listingKeys } from "@/hooks/buyers/buyerKeys";

/**
 * Tie a listing already in the catalog to this buyer.
 *
 * <p>Search-and-choose over the SHARED listing catalog rather than a create
 * form: a listing is a catalog record many buyers can be looking at, so the
 * ordinary case is finding one that already exists. Creating a new one is the
 * sibling action in the tab header, which runs the existing paste-to-create
 * flow and then ties the result.</p>
 *
 * <p>🔴 A duplicate pair is REFUSED by the server (400) — as a submission's
 * presented pair is too (ruling R1) — the same listing twice on one buyer's
 * list is nothing but a duplicate row. The refusal is surfaced verbatim
 * rather than pre-empted client-side, so the two can never disagree about what
 * counts as a duplicate.</p>
 */
export function TieExistingListingModal({
  buyerLeadId,
  open,
  onClose,
}: {
  buyerLeadId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState<ListingSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = { page: 1, pageSize: 10, q: search.trim() || undefined };
  const results = useQuery({
    queryKey: listingKeys.list(params),
    queryFn: () => listingsApi.list(params),
    enabled: open,
  });

  const close = () => {
    setSearch("");
    setNote("");
    setSelected(null);
    setError(null);
    onClose();
  };

  const tie = useMutation({
    mutationFn: () =>
      buyersApi.associateListing(buyerLeadId, {
        listingId: selected!.id,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      // The whole buyer prefix: the tab's list lives under it, and so does the
      // detail record whose counts a new association changes.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      // A sentence naming a record wants the record's NAME; the pitch mid-toast
      // reads as a run-on. Internal copy, not the outbound prose where the
      // pitch is the point.
      toast.success(
        `${resolvedShortListingName(selected?.displayName, selected?.businessName) ?? "Listing"} is now on this buyer's list.`,
      );
      close();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  const rows = results.data?.data ?? [];

  return (
    <Modal
      open={open}
      onClose={() => (tie.isPending ? undefined : close())}
      title="Tie an existing listing"
      description="Search the listing catalog. Everything on this list is something the buyer is looking at — presenting it is a separate step."
      size="md"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={close} disabled={tie.isPending}>
            Cancel
          </Button>
          <Button
            data-testid="tie-listing-submit"
            disabled={!selected || tie.isPending}
            loading={tie.isPending}
            onClick={() => {
              setError(null);
              tie.mutate();
            }}
          >
            {tie.isPending ? "Tying…" : "Tie listing"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <label className="block">
          <span className="text-caption uppercase tracking-wide text-gray-mid">
            Search listings
          </span>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Business name, headline, industry…"
            aria-label="Search listings"
            data-testid="tie-listing-search"
            className="mt-space-1"
          />
        </label>

        {results.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : results.isError ? (
          <div className="border border-border-light rounded-lg p-space-3">
            <p className="text-small text-status-error m-0 mb-space-2">
              Couldn&apos;t search the listing catalog.
            </p>
            <Button size="sm" variant="secondary" onClick={() => void results.refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-small text-gray-mid m-0" data-testid="tie-listing-no-results">
            No listings match. Try a different search, or create the listing
            instead.
          </p>
        ) : (
          <ul
            className="grid gap-space-1 list-none p-0 m-0 max-h-72 overflow-y-auto"
            data-testid="tie-listing-results"
          >
            {rows.map((l) => {
              const isSelected = selected?.id === l.id;
              return (
                <li key={l.id}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelected(l)}
                    className={`w-full text-left px-space-3 py-space-2 rounded-md border ${
                      isSelected
                        ? "border-onyx bg-white-warm"
                        : "border-border-light hover:bg-white-warm"
                    }`}
                  >
                    {/* SHORT — a picker row, with the location/industry/price
                        caption right below it doing the disambiguating. */}
                    <span className="block text-small text-onyx">
                      {shortenListingName(l.displayName) ?? listingShortName(l)}
                    </span>
                    <span className="block text-caption text-gray-mid">
                      {[
                        [l.city, l.state].filter(Boolean).join(", "),
                        l.industry,
                        l.askingPrice != null ? formatCurrency(l.askingPrice) : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "No details on file"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <label className="block">
          <span className="text-caption uppercase tracking-wide text-gray-mid">
            Note (optional)
          </span>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why this one is worth a look"
            aria-label="Note"
            data-testid="tie-listing-note"
            className="mt-space-1"
          />
        </label>

        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
